import React from "react";

// Customizable Area Start
import {
  Box,
  Grid,
  Paper,
  styled,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Snackbar,
  CircularProgress,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";

import { colors } from "../../../blocks/utilities/src/Colors";
// Customizable Area End

import ReportSchedulerCreateController, {
  Props,
  IScheduleFormData,
} from "./ReportSchedulerCreateController";

const Strings = {
  pageTitle: "Add Schedule",
  editPageTitle: "Edit Schedule",
  save: "Save",
  cancel: "Cancel",
  name: "Name",
  reportType: "Report Type",
  frequency: "Frequency",
  executionTime: "Execution Time",
  timezone: "Timezone",
  startDate: "Start Date",
  endDate: "End Date (Optional)",
  emailSubject: "Email Subject",
  emailBody: "Email Body",
  status: "Status",
  customParams: "Custom Params (JSON)",
  recipientsTo: "Recipients (To)",
  recipientsCc: "Recipients (CC)",
  recipientsToHelp: "Comma-separated email addresses",
  recipientsCcHelp: "Comma-separated email addresses (optional)",
};

export default class ReportSchedulerCreate extends ReportSchedulerCreateController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  renderTextField = (
    field: keyof IScheduleFormData,
    label: string,
    options?: {
      type?: string;
      multiline?: boolean;
      rows?: number;
      helperText?: string;
      placeholder?: string;
    }
  ) => {
    const { formData, errors } = this.state;
    return (
      <TextField
        fullWidth
        variant="outlined"
        label={label}
        value={formData[field]}
        onChange={(e) => this.handleInputChange(field, e.target.value)}
        error={!!errors[field]}
        helperText={errors[field] || options?.helperText}
        type={options?.type || "text"}
        multiline={options?.multiline}
        rows={options?.rows}
        placeholder={options?.placeholder}
        InputLabelProps={
          options?.type === "date" || options?.type === "time"
            ? { shrink: true }
            : undefined
        }
      />
    );
  };

  renderSelect = (
    field: keyof IScheduleFormData,
    label: string,
    options: { id: string; name: string }[]
  ) => {
    const { formData, errors } = this.state;
    return (
      <FormControl fullWidth variant="outlined" error={!!errors[field]}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={formData[field]}
          onChange={(e) =>
            this.handleInputChange(field, e.target.value as string)
          }
          label={label}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        {errors[field] && <FormHelperText>{errors[field]}</FormHelperText>}
      </FormControl>
    );
  };
  // Customizable Area End

  render() {
    // Customizable Area Start
    const { isSaving, snackbarOpen, snackbarMessage, snackbarSeverity, isEdit, isLoading } =
      this.state;
    // Customizable Area End

    return (
      // Customizable Area Start
      <MainBoxWrapper>
        <Box className="pageWrapper">
          <div style={webStyle.headerWrapper}>
            <PageTitle>
              {isEdit ? Strings.editPageTitle : Strings.pageTitle}
            </PageTitle>
          </div>

          <Paper style={webStyle.formContainer}>
            <Grid container spacing={3}>
              {/* Name */}
              <Grid item xs={12} md={6}>
                {this.renderTextField("name", Strings.name)}
              </Grid>

              {/* Report Type */}
              <Grid item xs={12} md={6}>
                {this.renderSelect(
                  "report_type",
                  Strings.reportType,
                  this.getReportTypeOptions()
                )}
              </Grid>

              {/* Frequency */}
              <Grid item xs={12} md={6}>
                {this.renderSelect(
                  "rrule",
                  Strings.frequency,
                  this.getRruleOptions()
                )}
              </Grid>

              {/* Execution Time */}
              <Grid item xs={12} md={6}>
                {this.renderTextField("execution_time", Strings.executionTime, {
                  type: "time",
                })}
              </Grid>

              {/* Timezone */}
              <Grid item xs={12} md={6}>
                {this.renderSelect(
                  "timezone",
                  Strings.timezone,
                  this.getTimezoneOptions()
                )}
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={6}>
                {this.renderSelect(
                  "status",
                  Strings.status,
                  this.getStatusOptions()
                )}
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} md={6}>
                {this.renderTextField("start_date", Strings.startDate, {
                  type: "date",
                })}
              </Grid>

              {/* End Date */}
              <Grid item xs={12} md={6}>
                {this.renderTextField("end_date", Strings.endDate, {
                  type: "date",
                })}
              </Grid>

              {/* Email Subject */}
              <Grid item xs={12}>
                {this.renderTextField("email_subject", Strings.emailSubject, {
                  placeholder: "{{report_type}} - {{date}}",
                })}
              </Grid>

              {/* Email Body */}
              <Grid item xs={12}>
                {this.renderTextField("email_body", Strings.emailBody, {
                  multiline: true,
                  rows: 3,
                })}
              </Grid>

              {/* Recipients To */}
              <Grid item xs={12} md={6}>
                {this.renderTextField("recipients_to", Strings.recipientsTo, {
                  helperText: Strings.recipientsToHelp,
                  placeholder: "user1@example.com, user2@example.com",
                })}
              </Grid>

              {/* Recipients CC */}
              <Grid item xs={12} md={6}>
                {this.renderTextField("recipients_cc", Strings.recipientsCc, {
                  helperText: Strings.recipientsCcHelp,
                  placeholder: "user3@example.com",
                })}
              </Grid>

              {/* Custom Params */}
              <Grid item xs={12}>
                {this.renderTextField("custom_params", Strings.customParams, {
                  multiline: true,
                  rows: 8,
                })}
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <div style={webStyle.buttonContainer}>
                  <Button
                    variant="outlined"
                    className="cancelButton"
                    onClick={this.handleCancel}
                    disabled={isSaving}
                  >
                    {Strings.cancel}
                  </Button>
                  <Button
                    variant="contained"
                    className="saveButton"
                    onClick={this.handleSubmit}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      Strings.save
                    )}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={this.handleCloseSnackbar}
            severity={snackbarSeverity}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </MainBoxWrapper>
      // Customizable Area End
    );
  }
}

// Customizable Area Start
export const PageTitle = styled("div")({
  fontFamily: "Montserrat",
  fontStyle: "normal",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "29px",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const MainBoxWrapper = styled("div")({
  "& .saveButton": {
    background: colors().cyancobaltblue,
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    color: colors().white,
    height: "56px",
    minWidth: "150px",
    borderRadius: "8px",
    textTransform: "unset",
    "&:hover": {
      background: colors().cyancobaltblue,
    },
    "&:disabled": {
      background: colors().lightborder,
    },
    "@media only screen and (max-width: 1024px)": {
      fontSize: "13px",
      height: "44px",
    },
  },
  "& .cancelButton": {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    color: colors().cyancobaltblue,
    height: "56px",
    minWidth: "150px",
    borderRadius: "8px",
    textTransform: "unset",
    borderColor: colors().cyancobaltblue,
    marginRight: "16px",
    "@media only screen and (max-width: 1024px)": {
      fontSize: "13px",
      height: "44px",
    },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
  },
  "& .MuiInputLabel-outlined": {
    fontFamily: "Montserrat",
  },
  "& .MuiOutlinedInput-input": {
    fontFamily: "Montserrat",
  },
});

export const webStyle = {
  headerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 24,
    alignItems: "center",
    flexWrap: "wrap" as "wrap",
  },
  formContainer: {
    padding: "24px",
    borderRadius: "8px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "16px",
  },
};
// Customizable Area End
